import {
  KitButton,
  KitListItem,
  KitLoader,
  KitText,
} from '@omni/kit/components';
import { EventImage } from '@omni/kit/components/EventImage';
import KitBar from '@omni/kit/components/KitBar';
import KitModalV2 from '@omni/kit/components/KitModalV2';
import {
  SizeClass,
  SizeClassV2,
  useSizeClass,
} from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import { IGroupEvent } from '@omni/kit/services/GroupsService/Types';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import {
  NavigationProp,
  ParamListBase,
  RouteProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import React, { ReactElement, useCallback, useState } from 'react';
import { Platform, SectionList, View } from 'react-native';
import { useSelector } from 'react-redux';

import {
  IGroupEventAllListProps,
  SendbirdUserMessage,
} from '../../../../Types';
import type {
  Event,
  GroupedEventSection,
} from '../../../../hooks/useTransformGroupEvents';
import * as Constants from '../../../Constants';
import { channelSelector } from '../../../redux/selectors';
import CreateOrUpdateGroupAttendance from '../../events/components/CreateOrUpdateGroupAttendance';
import GroupEventsEmptyState from './GroupEventsEmptyState';

export interface RouteProps extends RouteProp<ParamListBase, string> {
  params: {
    eventId: any;
    activeMessage: SendbirdUserMessage;
    isRepeatingEvent: boolean;
    takeAttendance?: boolean;
  };
}

export default function GroupAllListEvents({
  sections,
  onRefresh,
  isRefreshing,
  onPressCreateGroupEvent,
  onEndReached,
  isPaginating,
  upcoming,
}: IGroupEventAllListProps): ReactElement {
  const route = useRoute<RouteProps>();
  const { t } = useTranslation();
  const channel = useSelector(channelSelector) as GroupChannel;
  const isManager = channel?.myRole === 'operator';
  const { sizeClassV2 } = useSizeClass();
  const isLarge = sizeClassV2 >= SizeClassV2.L;
  const [attendanceMode, setAttendanceMode] = React.useState<'create' | 'edit'>(
    'create'
  );
  const [groupEvent, setGroupEvent] = useState<IGroupEvent | undefined>();
  const { sizeClass } = useSizeClass();
  const isSmall = sizeClass === SizeClass.Small;
  const [isAttendanceModalVisible, setAttendanceModalVisible] = useState(false);
  // Navigation
  let navigation: NavigationProp<ParamListBase>;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigation = useNavigation();
  } catch {
    navigation = {} as NavigationProp<ParamListBase>; // Provide a fallback object
  }

  const handleAttendancePress = useCallback(
    (mode: 'create' | 'edit', groupEventParam?: any) => {
      if (Platform.OS === 'web') {
        setAttendanceMode(mode);
        setGroupEvent(groupEventParam || null);
        setAttendanceModalVisible(true);
      } else {
        navigation.navigate(Constants.CREATE_EDIT_GROUP_ATTENDANCE_SCREEN, {
          title:
            mode === 'edit'
              ? t('groups:editAttendance')
              : t('groups:takeAttendanceButton'),
          mode,
          groupEvent: groupEventParam,
        });
      }
    },
    [navigation, t]
  );
  const handleCancel = () => {
    setAttendanceModalVisible(false);
  };

  return (
    <View
      style={[
        {
          flex: 1,
        },
        isLarge && {
          marginHorizontal: Spacing.xxxxl - 10,
          marginTop: 6,
        },
      ]}
    >
      <SectionList
        onEndReachedThreshold={0.1}
        onEndReached={
          sections?.length && sections[0]?.data?.length > 0
            ? onEndReached
            : undefined
        }
        renderItem={({ item }: { item: Event }) => {
          const missed =
            item._embedded?.sessions?.[0]?._embedded?.counts?.missed || 0;
          const total =
            item._embedded?.sessions?.[0]?._embedded?.counts?.total || 0;

          return (
            <>
              <KitListItem
                ImageComponent={
                  <EventImage
                    url={item?.image}
                    date={item?.start_at}
                    timezone={item?.timezone}
                  />
                }
                title={item.title}
                subtitleLength={2}
                secondarySubtitle={item.eventSubtitle}
                rightActionIcon='action-h'
                showRightIconOnAndroid={true}
                minHeight={60}
                style={{
                  paddingHorizontal: Spacing.l,
                  paddingTop: Spacing.l,
                }}
                bottomBorder={false}
                onPress={() =>
                  navigation.navigate(Constants.EVENT_DETAILS_SCREEN, {
                    eventId: item.id,
                  })
                }
              />
              {item._embedded?.sessions?.[0]?._embedded?.counts && isManager ? (
                <View
                  style={{
                    paddingHorizontal: Spacing.l,
                    paddingBottom: Spacing.l,
                    borderBottomColor: Colors.N100,
                    borderBottomWidth: 1,
                  }}
                >
                  <KitBar
                    size='md'
                    type='stacked'
                    legend='inline'
                    segments={[
                      {
                        value: total,
                        color: Colors.N900,
                        label: t('groups:attended'),
                        labelValue: total,
                      },
                      {
                        value: missed,
                        color: Colors.N100,
                        label: t('groups:missed'),
                        labelValue: missed,
                      },
                    ]}
                  />
                </View>
              ) : (!item._embedded?.sessions?.[0]?._embedded?.counts ||
                  item._embedded?.sessions?.[0]?._embedded?.counts
                    .total_headcount === 0) &&
                isManager ? (
                <KitButton
                  testID='attendance-bar-button'
                  secondary
                  iconSize={14}
                  title={t('groups:takeAttendanceButton')}
                  onPress={() => handleAttendancePress('create', item)}
                  style={{
                    width: '100%',
                    paddingHorizontal: Spacing.l,
                    borderBottomColor: Colors.N100,
                    borderBottomWidth: 1,
                    paddingBottom: Spacing.l,
                  }}
                />
              ) : null}
            </>
          );
        }}
        sections={sections}
        keyExtractor={(item, index) => (item.title ?? '') + index}
        ListEmptyComponent={
          <GroupEventsEmptyState
            isManager={isManager}
            onPressCreateGroupEvent={onPressCreateGroupEvent}
            upcoming={upcoming}
          />
        }
        ListFooterComponent={
          isPaginating ? (
            <View style={{ padding: Spacing.l }}>
              <KitLoader />
            </View>
          ) : null
        }
        onRefresh={onRefresh}
        refreshing={isRefreshing}
        renderSectionHeader={({ section }) => {
          // Get the index of the section
          const sectionIndex = sections?.findIndex(
            (sec: GroupedEventSection) =>
              sec.sectionHeader === section.sectionHeader
          );

          return (
            <View
              style={{
                marginTop: sectionIndex === 0 ? Spacing.m : Spacing.xxl, // No marginTop for the first section
                paddingHorizontal: Spacing.l,
              }}
            >
              <KitText bold fontSize={20} color={Colors.N900}>
                {section.sectionHeader}
              </KitText>
            </View>
          );
        }}
        showsVerticalScrollIndicator={false}
        stickySectionHeadersEnabled={false}
      />
      {Platform.OS === 'web' ? (
        <KitModalV2
          modalStyle={{
            width: '100%',
            maxWidth: 620,
            height: '100%',
            maxHeight: 700,
            padding: 0,
          }}
          coverScreen={true}
          anchorBottom={isSmall}
          isVisible={isAttendanceModalVisible}
          onClose={handleCancel}
          enableScroll
          noScrollView
        >
          <CreateOrUpdateGroupAttendance
            route={route}
            mode={attendanceMode}
            onCancel={handleCancel}
            onSuccess={onRefresh}
            groupEvent={groupEvent}
          />
        </KitModalV2>
      ) : null}
    </View>
  );
}
